import React, { useMemo, useState } from "react";
import { Multiselect, MultiselectProps } from "@amzn/awsui-components-react";
import { InvestigationFieldBreakdown } from "../../lusca-search-api/generated-src";
import { MAX_RULE_VALUES } from "./investigationUtils";

/** One selectable value of a rule field, as the population breakdown counted it. */
export interface RuleValueOption {
  value: string;
  count: number;
  /** Share of the population the breakdown was computed over, 0–100 (rounded). */
  share: number;
}

/**
 * The values a rule field can be pinned to, taken from a preview's `fieldBreakdown` (the same
 * distribution the Items tab shows as "Population breakdown"). NULL/empty is dropped: a
 * predicate cannot express "no value".
 */
export function ruleValueOptions(
  breakdown: InvestigationFieldBreakdown | undefined
): RuleValueOption[] {
  if (!breakdown) return [];
  const total = breakdown.totalCount || 0;
  return breakdown.values
    .filter((v) => v.value !== "")
    .map((v) => ({
      value: v.value,
      count: v.count,
      share: total > 0 ? Math.round((v.count / total) * 100) : 0,
    }));
}

export const formatOptionShare = (option: RuleValueOption): string =>
  `${option.share}% · ${option.count.toLocaleString()}`;

interface RuleValueFieldProps {
  label: string;
  /** The values the field is pinned to (one for `eq`, several for `in`). */
  values: string[];
  disabled?: boolean;
  placeholder?: string;
  options: RuleValueOption[];
  /** Distinct values in the population the options were counted over (may exceed `options`). */
  distinctValues?: number;
  onChange: (values: string[]) => void;
}

/**
 * Value picker for one population-rule field: a Multiselect whose pinned values show as tokens
 * inside the control. The list offers the values the population actually contains (with their
 * share) so nobody has to know a GL code or queue name by heart; typing a value the bounded
 * breakdown does not list offers it as `Use "…"`. Pinning a second value turns the predicate into
 * "is one of". Values are unpinned by unticking them in the list. "Select all" ticks (or unticks)
 * every value the current search shows, so "all but one" is Select all + untick one.
 *
 * Filtering is done here rather than by Cloudscape so the `Use "…"` entry and the pinned free-text
 * values (which the breakdown never lists) can be part of the option list.
 */
export const RuleValueField: React.FC<RuleValueFieldProps> = ({
  label,
  values,
  disabled,
  placeholder,
  options,
  distinctValues,
  onChange,
}) => {
  const [text, setText] = useState("");
  const full = values.length >= MAX_RULE_VALUES;
  const hidden = Math.max(0, (distinctValues ?? 0) - options.length);

  const listed = useMemo(() => new Set(options.map((o) => o.value)), [options]);
  const shown = useMemo<MultiselectProps.Option[]>(() => {
    const typed = text.trim();
    const lower = typed.toLowerCase();
    const pinned = new Set(values);
    const canAdd = (value: string) => pinned.has(value) || !full;
    const result: MultiselectProps.Option[] = [];
    // Free text the breakdown does not list, offered first so Enter takes it.
    if (typed && !listed.has(typed) && !pinned.has(typed)) {
      result.push({
        value: typed,
        label: `Use "${typed}"`,
        disabled: full,
        disabledReason: full ? `At most ${MAX_RULE_VALUES} values` : undefined,
      });
    }
    // Pinned values the breakdown never listed stay in the list so they can be unticked.
    values
      .filter((v) => !listed.has(v) && v.toLowerCase().includes(lower))
      .forEach((v) => result.push({ value: v, label: v }));
    options
      .filter((o) => o.value.toLowerCase().includes(lower))
      .forEach((o) =>
        result.push({
          value: o.value,
          label: o.value,
          description: formatOptionShare(o),
          disabled: !canAdd(o.value),
          disabledReason: canAdd(o.value)
            ? undefined
            : `At most ${MAX_RULE_VALUES} values`,
        })
      );
    return result;
  }, [text, values, options, listed, full]);

  const selected = useMemo<MultiselectProps.Option[]>(
    () => values.map((v) => ({ value: v, label: v })),
    [values]
  );

  return (
    <div data-testid={`rule-value-${label}`} style={{ minWidth: 0 }}>
      <Multiselect
        ariaLabel={`${label} value`}
        inlineTokens
        disabled={disabled}
        placeholder={placeholder}
        selectedOptions={selected}
        options={shown}
        filteringType="manual"
        filteringPlaceholder="Find or type a value"
        statusType="finished"
        finishedText={
          hidden > 0
            ? `${hidden.toLocaleString()} more ${
                hidden === 1 ? "value" : "values"
              } not listed — type one to use it`
            : undefined
        }
        empty={
          options.length
            ? "No listed value matches"
            : "Values appear once the preview has run"
        }
        enableSelectAll
        i18nStrings={{ selectAllText: "Select all" }}
        onLoadItems={({ detail }) => setText(detail.filteringText)}
        onChange={({ detail }) => {
          let next = detail.selectedOptions
            .map((o) => o.value ?? "")
            .filter((v) => v !== "");
          const pinned = new Set(values);
          const added = next.filter((v) => !pinned.has(v));
          // "Select all" adds every shown option at once; it must not also pin the
          // `Use "…"` free-text entry (the search text), only the listed values.
          const typed = text.trim();
          if (added.length > 1 && typed && !listed.has(typed)) {
            next = next.filter((v) => v !== typed || pinned.has(v));
          }
          // Keep already-pinned values first and never exceed the rule's value cap.
          if (next.length > MAX_RULE_VALUES) {
            next = [
              ...next.filter((v) => pinned.has(v)),
              ...next.filter((v) => !pinned.has(v)),
            ].slice(0, MAX_RULE_VALUES);
          }
          onChange(next);
        }}
        onBlur={() => setText("")}
      />
    </div>
  );
};

export default RuleValueField;
